import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Phone, MapPin, Briefcase, ArrowRight, FileText, Crown, Banknote, Settings, Users, CheckCircle, Bell } from 'lucide-react';
import { useAdvisorStore } from '../../store/advisorStore';
import { supabase } from '../../lib/supabase';
import { ProfileType } from '../../types';
import type { AdvisorSignupForm as AdvisorSignupFormData } from '../../types/advisor';

interface ProfileOption {
  type: ProfileType;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  requirements: string[];
}

const profileOptions: ProfileOption[] = [
  {
    type: ProfileType.ACCOMPAGNATEUR,
    title: 'Accompagnateur',
    description: 'J\'accompagne les porteurs de projet dans leur démarche ESS',
    icon: Users,
    color: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
    requirements: [
      'Expérience en accompagnement ou conseil',
      'Connaissance de l\'ESS',
      'Disponibilité pour le suivi de projets'
    ]
  },
  {
    type: ProfileType.COORDINATEUR,
    title: 'Coordinateur CRESS',
    description: 'Je coordonne l\'accompagnement au sein de la CRESS',
    icon: Crown,
    color: 'bg-purple-50 border-purple-200 hover:bg-purple-100',
    requirements: [
      'Expérience en coordination d\'équipes',
      'Connaissance approfondie de l\'ESS',
      'Capacités de gestion et d\'organisation'
    ]
  },
  {
    type: ProfileType.FINANCEUR,
    title: 'Financeur',
    description: 'J\'évalue et finance des projets ESS',
    icon: Banknote,
    color: 'bg-orange-50 border-orange-200 hover:bg-orange-100',
    requirements: [
      'Expérience en évaluation de projets',
      'Connaissance des mécanismes de financement',
      'Capacité d\'analyse financière'
    ]
  },
  {
    type: ProfileType.ADMIN,
    title: 'Administrateur',
    description: 'Je gère la plateforme et les utilisateurs',
    icon: Settings,
    color: 'bg-red-50 border-red-200 hover:bg-red-100',
    requirements: [
      'Compétences techniques avancées',
      'Expérience en administration système',
      'Responsabilités de gestion de plateforme'
    ]
  }
];

export const AdvisorSignupForm: React.FC = () => {
  const navigate = useNavigate();
  const { 
    territories, 
    sectors, 
    isLoading, 
    error, 
    fetchTerritories, 
    fetchSectors, 
    submitAdvisorRequest,
    clearError 
  } = useAdvisorStore();

  const [formData, setFormData] = useState<AdvisorSignupFormData>({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: '',
    territory: '',
    sector: '',
    motivation: '',
    experience: ''
  });
  const [selectedProfile, setSelectedProfile] = useState<ProfileType | null>(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState<'profile' | 'form'>('profile');

  useEffect(() => {
    fetchTerritories();
    fetchSectors();
    clearError();
  }, []);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    if (!formData.password) {
      errors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 6) {
      errors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (formData.password !== confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.full_name.trim()) {
      errors.full_name = 'Le nom complet est requis';
    }

    if (!selectedProfile) {
      errors.profile = 'Veuillez sélectionner un type de profil';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Submit advisor request
      await submitAdvisorRequest({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: formData.phone,
        role: selectedProfile || ProfileType.ACCOMPAGNATEUR,
        motivation: formData.motivation,
        experience: formData.experience,
        organization: formData.organization
      });

      // Navigate to success page or login
      navigate('/advisor-signup-success');
    } catch (err: any) {
      console.error('Signup error:', err);
    }
  };

  const handleChange = (field: keyof AdvisorSignupFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleProfileSelect = (profileType: ProfileType) => {
    setSelectedProfile(profileType);
    setCurrentStep('form');
  };

  const handleBackToProfileSelection = () => {
    setCurrentStep('profile');
    setSelectedProfile(null);
  };

  if (currentStep === 'profile') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-8">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <img 
                src="/logo_CRESS_Guadeloupe.jpg" 
                alt="CRESS Guadeloupe" 
                className="h-24 w-auto object-contain"
              />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Rejoindre l'équipe CRESS</h1>
            <p className="text-xl text-gray-600">Choisissez le profil qui correspond à votre rôle souhaité</p>
          </div>

          {/* Profile Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {profileOptions.map((option) => {
              const IconComponent = option.icon;
              
              return (
                <div
                  key={option.type}
                  onClick={() => handleProfileSelect(option.type)}
                  className={`
                    relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-200 transform hover:scale-105 hover:shadow-lg
                    ${option.color}
                  `}
                >
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 mx-auto rounded-full bg-white flex items-center justify-center mb-4 shadow-lg">
                      <IconComponent className="w-8 h-8 text-gray-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{option.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{option.description}</p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">Prérequis :</p>
                    {option.requirements.map((requirement, index) => (
                      <div key={index} className="flex items-start text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-2 flex-shrink-0" />
                        {requirement}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
            <h3 className="font-semibold text-blue-900 mb-2">📋 Processus de validation</h3>
            <p className="text-blue-800 text-sm">
              Votre demande sera examinée par l'équipe CRESS sous 48h. 
              Vous recevrez un email de confirmation une fois votre profil validé.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              Déjà un compte ?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo_CRESS_Guadeloupe.jpg" 
              alt="CRESS Guadeloupe" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <div className="flex items-center justify-center space-x-3 mb-4">
            <button
              onClick={handleBackToProfileSelection}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              ← Changer de profil
            </button>
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              {React.createElement(profileOptions.find(p => p.type === selectedProfile)?.icon || Users, {
                className: "w-4 h-4 text-white"
              })}
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Inscription {profileOptions.find(p => p.type === selectedProfile)?.title}
          </h1>
          <p className="text-gray-600">Complétez vos informations pour soumettre votre demande</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Informations personnelles
              </h3>

              {/* Full Name */}
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.full_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Votre nom complet"
                  />
                </div>
                {validationErrors.full_name && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.full_name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="votre@email.com"
                  />
                </div>
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0590 XX XX XX"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      className={`w-full pl-12 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        validationErrors.password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmer le mot de passe *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full pl-12 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        validationErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {validationErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Information */}

            {/* Profile Requirements */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">
                Prérequis pour {profileOptions.find(p => p.type === selectedProfile)?.title}
              </h3>
              <ul className="text-sm text-blue-800 space-y-1">
                {profileOptions.find(p => p.type === selectedProfile)?.requirements.map((req, index) => (
                  <li key={index} className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-2 mt-2 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            {/* Validation Errors */}
            {validationErrors.profile && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{validationErrors.profile}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Soumettre ma demande de {profileOptions.find(p => p.type === selectedProfile)?.title}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Déjà un compte ?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Se connecter
              </Link>
            </p>
          </div>
        </div>

        {/* Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Votre demande de {profileOptions.find(p => p.type === selectedProfile)?.title.toLowerCase()} sera examinée sous 48h.
          </p>
        </div>
      </div>
    </div>
  );
};